import { useMutation } from '@apollo/client';
import { PackageMinus, PackagePlus } from 'lucide-react';
import React from 'react';
import { match } from 'ts-pattern';

import { useCompleteFragment } from '@eluve/apollo-client';
import { TooltipLabel } from '@eluve/blocks';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  P,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useIsEluveAdmin } from '@eluve/session-helpers';

export interface TogglePromptTemplateActiveActionProps {
  outputTemplateId: string;
}

const toggleOutputTemplateActiveMutation = graphql(`
  mutation toggleOutputTemplateActive($id: uuid!, $isActive: Boolean!) {
    updateLlmOutputTemplatesByPk(
      pkColumns: { id: $id }
      _set: { isActive: $isActive }
    ) {
      __typename
      id
      isActive
    }
  }
`);

export const outputTemplateActiveFragment = graphql(`
  fragment OutputTemplateActive on LlmOutputTemplates @_unmask {
    __typename
    id
    isActive
  }
`);

export const ToggleOutputTemplateActiveAction: React.FC<
  TogglePromptTemplateActiveActionProps
> = ({ outputTemplateId }) => {
  const toast = useToast();
  const isEluveAdmin = useIsEluveAdmin();
  const [updatePromptTemplateIsActive] = useMutation(
    toggleOutputTemplateActiveMutation,
    {
      optimisticResponse: (data) => ({
        updateLlmOutputTemplatesByPk: {
          __typename: 'LlmOutputTemplates' as const,
          id: data.id,
          isActive: data.isActive,
        },
      }),
      onError: () => {
        toast.error('Failed to update Output Template');
      },
    },
  );

  const outputTemplateSettings = useCompleteFragment({
    fragment: outputTemplateActiveFragment,
    key: {
      id: outputTemplateId,
    },
  });

  const toggleActive = async () => {
    await updatePromptTemplateIsActive({
      variables: {
        id: outputTemplateId,
        isActive: !outputTemplateSettings.isActive,
      },
    });
  };

  const { label, description, action, Component } = match(
    outputTemplateSettings.isActive,
  )
    .with(true, () => {
      const description = (
        <div>
          <P>
            Marking this Output Template as inactive will mean users can no
            longer use it for generating LLM outputs
          </P>
        </div>
      );

      return {
        label: 'Mark this Output Template as inactive',
        description,
        action: 'Deactivate',
        Component: <PackageMinus className="text-bg-orange-strong" />,
      };
    })
    .otherwise(() => ({
      label: 'Mark this Output Template as active',
      description: (
        <P>
          Marking this Output Template as active will make it available for
          users
        </P>
      ),
      action: 'Activate',
      Component: <PackagePlus className="text-bg-lightgreen-strong" />,
    }));

  if (!isEluveAdmin) {
    return null;
  }

  return (
    <AlertDialog>
      <TooltipLabel label={label}>
        <AlertDialogTrigger asChild>
          <Button size="icon" variant="outline">
            {Component}
          </Button>
        </AlertDialogTrigger>
      </TooltipLabel>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-text-secondary">
            Are you sure?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel autoFocus>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-bg-negative-strong-primary text-text-negative-strong hover:bg-bg-negative-strong-primary/90"
            onClick={toggleActive}
          >
            {action}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
